/* ============================================================
   Shared "type/setup" card picker — the icon + name + description button
   grid used by both the legacy product/offer wizard (ProductDetailDrawer.tsx,
   phases 'type'/'subtype', still needed there for editing offers saved
   before SAM-198/SAM-545) and the new Services create flow
   (src/pages/ServiceEditor/create/**, SAM-198).

   ProductDetailDrawer.tsx is a BaseUI/Styletron-frozen file (SAM-175) and
   its role-grid/role-opt markup previously lived inline there, scoped to
   `.pw-form` in product/productWizard.css. ServiceEditorPage.tsx is a
   Tailwind + src/styles/tokens.css page and cannot depend on that scoped
   legacy CSS (or on baseui/styletron-react — this component imports
   neither). Extracted here as ONE presentational implementation, styled
   with tokens.css variables so it renders correctly in both call sites,
   instead of keeping two parallel card-grid implementations in sync by
   hand.
   ============================================================ */
import React from 'react';

// `any` here matches ProductDetailDrawer.tsx's own `IconC` alias — lucide-react's icon type
// (ForwardRefExoticComponent<... & RefAttributes<SVGSVGElement>>) isn't structurally assignable to
// a plain `ComponentType<{ size?: number }>` (its `propTypes.size` validator is typed wider), so a
// precise prop type here would reject every lucide icon passed in from either call site.
export interface OfferPickerItem {
  k: string;
  n: string;
  d: string;
  /** Optional last line, e.g. the presets a service setup applies (SAM-198 mock 2 v2). */
  preset?: string;
  Icon: React.ComponentType<any>;
}

export const OfferPickerCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  preset?: string;
  onClick: () => void;
}> = ({ icon, title, description, preset, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-start gap-3 text-left p-4 rounded-12 bg-white transition-colors cursor-pointer"
    style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-12)', minHeight: 44 }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--tenant-primary, #111827)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--line)'; }}
  >
    <span
      className="flex items-center justify-center shrink-0"
      style={{
        width: 32, height: 32, borderRadius: 'var(--r-8)', background: 'var(--surface)',
        color: 'var(--tenant-primary, #111827)',
      }}
    >
      {icon}
    </span>
    <span>
      <span className="block text-[13px] font-semibold" style={{ color: 'var(--ink-900)' }}>{title}</span>
      <span className="block text-[11px] mt-0.5 leading-snug" style={{ color: 'var(--ink-500)' }}>{description}</span>
      {preset && (
        <span className="block text-[11px] mt-1.5 font-semibold" style={{ color: 'var(--tenant-primary, #005488)' }}>{preset}</span>
      )}
    </span>
  </button>
);

export const OfferPickerGrid: React.FC<{
  items: OfferPickerItem[];
  onPick: (key: string) => void;
}> = ({ items, onPick }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
    {items.map((o) => (
      <OfferPickerCard
        key={o.k}
        icon={<o.Icon size={16} />}
        title={o.n}
        description={o.d}
        preset={o.preset}
        onClick={() => onPick(o.k)}
      />
    ))}
  </div>
);

export default OfferPickerGrid;
